import React, { useState } from 'react';
import { Card, CardContent, Typography, Dialog, DialogTitle, Divider, DialogContent, Grid2 as Grid, InputLabel, IconButton, Grow, DialogActions, Button } from '@mui/material';
import PageviewOutlinedIcon from '@mui/icons-material/PageviewOutlined';
import SharedMaterialReactTable from '../../../Shared/MaterialReactTable';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
// Styled Tooltip component (copied from SubmissionHome)
const StyledTooltip = styled(({ className, ...props }: any) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .MuiTooltip-tooltip`]: {
        backgroundColor: '#f2f2f2',
        color: 'rgba(0, 0, 0, 0.87)',
        boxShadow: theme.shadows[16],
        fontSize: 11,
        fontStyle: 'oblique'
    },
}));

interface ValidationError {
    measureCode?: string;
    shortIndicatorName?: string;
    severity?: string;
    ruleName?: string;
    indicatorKey?: string;
    message?: string;
}

interface Validation {
    validationErrors: ValidationError[];
    validationText?: string;
}

interface SubmissionDownload {
    displayName: string;
    originalName: string;
}

interface Props {
    validation: Validation;
    submissionData: any;
    component: string;
    submissionDownloads: SubmissionDownload[];
    classes: any;
}

const ValidationTable: React.FC<Props> = ({ validation, submissionData, component, submissionDownloads, classes }) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState<ValidationError>({ measureCode: '', shortIndicatorName: '', severity: '', ruleName: '', indicatorKey: '', message: '' });

    const handleShowDialog = (item: ValidationError) => {
        setCurrentItem(item);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
    };

    if (!validation.validationErrors || validation.validationErrors.length === 0) {
        return (
            <Card className={classes.card}>
                <CardContent>
                    <Typography className={classes.primaryBlue + " " + classes.marginBottom}>
                        Validation Errors
                    </Typography>
                    <Typography variant="body2">No validation errors found.</Typography>
                </CardContent>
            </Card>
        );
    }

    const showMeasureCode = validation.validationErrors.some(x => x.measureCode);
    const showShortIndicatorName = validation.validationErrors.some(x => x.shortIndicatorName);
    const showSeverity = validation.validationErrors.some(x => x.severity);
    const showRuleName = validation.validationErrors.some(x => x.ruleName);

    const tableData = validation.validationErrors.map(error => ({
        measureCode: error.measureCode || '',
        shortIndicatorName: error.shortIndicatorName || '',
        severity: error.severity || '',
        ruleName: error.ruleName || '',
        indicatorKey: error.indicatorKey || '',
        message: error.message || '',
        details: error
    }));

    const columns: any[] = [];

    const tooltipCell = (value: string, error: ValidationError) => (
        <StyledTooltip title={(error.indicatorKey ? `[IndicatorKey:${error.indicatorKey}] ` : '') + error.message}>
            <div>{value}</div>
        </StyledTooltip>
    );

    if (showMeasureCode) {
        columns.push({ size: 92, accessorKey: 'measureCode', header: 'Measure', enableFiltering: true, Cell: ({ row }: any) => tooltipCell(row.original.measureCode, row.original.details) });
    }
    if (showShortIndicatorName) {
        columns.push({ accessorKey: 'shortIndicatorName', header: 'Indicator', enableFiltering: true, Cell: ({ row }: any) => tooltipCell(row.original.shortIndicatorName, row.original.details) });
    }
    if (showSeverity) {
        columns.push({ size: 90, accessorKey: 'severity', header: 'Severity', enableFiltering: true, Cell: ({ row }: any) => showRuleName ? tooltipCell(row.original.severity, row.original.details) : <div>{row.original.severity}</div> });
    }
    if (showRuleName) {
        columns.push({ accessorKey: 'ruleName', header: 'Rule Name', enableFiltering: true, Cell: ({ row }: any) => tooltipCell(row.original.ruleName, row.original.details) });
        columns.push({ size: 78,
            accessorKey: 'details', header: 'Details', enableFiltering: false, Cell: ({ row }: any) => (
                <StyledTooltip title="Show Details" aria-label="validationDetail">
                    <IconButton size='small' aria-label="validationDetail" onClick={() => handleShowDialog(row.original.details)}>
                        <PageviewOutlinedIcon fontSize="inherit" />
                    </IconButton>
                </StyledTooltip>
            )
        });
    } else {
        columns.push({ accessorKey: 'message', header: 'Message', enableFiltering: false });
    }

    return (
        <div>
            <Dialog open={dialogOpen} onClose={handleCloseDialog} className={classes.dialogM} TransitionComponent={Grow}>
                <DialogTitle><Typography className={classes.primaryBlueMedium}>Validation Details</Typography></DialogTitle>
                <Divider />
                <DialogContent>
                    <Grid container spacing={2}>
                        {currentItem.measureCode && (
                            <Grid size={12}><InputLabel shrink>Measure Code</InputLabel><Typography variant="subtitle1">{currentItem.measureCode}</Typography></Grid>
                        )}
                        {currentItem.shortIndicatorName && (
                            <Grid size={12}><InputLabel shrink>Short Indicator Name</InputLabel><Typography variant="subtitle1">{currentItem.shortIndicatorName}</Typography></Grid>
                        )}
                        {currentItem.indicatorKey && (
                            <Grid size={12}><InputLabel shrink>Indicator Key</InputLabel><Typography variant="subtitle1">{currentItem.indicatorKey}</Typography></Grid>
                        )}
                        {currentItem.severity && (
                            <Grid size={12}><InputLabel shrink>Severity</InputLabel><Typography variant="subtitle1">{currentItem.severity}</Typography></Grid>
                        )}
                        {currentItem.ruleName && (
                            <Grid size={12}><InputLabel shrink>Rule Name</InputLabel><Typography variant="subtitle1">{currentItem.ruleName}</Typography></Grid>
                        )}
                        {currentItem.message && (
                            <Grid size={12}><InputLabel shrink>Message</InputLabel><Typography variant="subtitle2">{currentItem.message}</Typography></Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions><Button onClick={handleCloseDialog}>Close</Button></DialogActions>
            </Dialog>
            <Grid container spacing={1}>

                <Grid size={12}>
                    <SharedMaterialReactTable 
                        title='Validations' data={tableData} 
                        columns={columns} 
                        enableRowSelection={false} 
                        layoutMode='fixed'
                        enableFiltering={component.includes("DST") || component.includes("HEDIS")} 
                        initialState={{ pagination: { pageSize: 10, pageIndex: 0 } }} 
                    />
                </Grid>
            </Grid>
        </div>
    );
};

export default ValidationTable;
